import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Calendar, Users, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import frLocale from '@fullcalendar/core/locales/fr';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_COLORS = {
  'congé': { bg: '#dbeafe', text: '#1d4ed8', dot: '#3b82f6' },
  'conge': { bg: '#dbeafe', text: '#1d4ed8', dot: '#3b82f6' },
  'maladie': { bg: '#fee2e2', text: '#dc2626', dot: '#ef4444' },
  'formation': { bg: '#ede9fe', text: '#7c3aed', dot: '#8b5cf6' },
  'personnel': { bg: '#fef3c7', text: '#d97706', dot: '#f59e0b' },
  'maternite': { bg: '#fce7f3', text: '#db2777', dot: '#ec4899' },
  'paternite': { bg: '#cffafe', text: '#0891b2', dot: '#06b6d4' },
  'autre': { bg: '#f3f4f6', text: '#4b5563', dot: '#6b7280' },
};

const AbsenceCalendarPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [rolloverHistory, setRolloverHistory] = useState([]);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const year = new Date().getFullYear();
      const res = await axios.get(`${API}/api/leaves/calendar?year=${year}`);
      setEvents(res.data || []);
    } catch {
      toast.error("Erreur chargement calendrier");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadEmployees = async () => {
    try {
      const res = await axios.get(`${API}/api/employees`);
      setEmployees((res.data || []).filter(e => e.is_active));
    } catch {}
  };

  const loadRolloverHistory = async () => {
    try {
      const res = await axios.get(`${API}/api/leaves/rollover-history`);
      setRolloverHistory(res.data || []);
    } catch {}
  };

  useEffect(() => { loadEvents(); loadEmployees(); loadRolloverHistory(); }, [loadEvents]);

  const handleRollover = async () => {
    if (!window.confirm("Etes-vous sur de vouloir effectuer le report annuel des conges ? Les soldes actuels seront reportes et 20 jours seront ajoutes.")) return;
    try {
      const res = await axios.post(`${API}/api/leaves/annual-rollover`, { annual_entitlement: 20 });
      toast.success(`Report effectue pour ${res.data.employees_processed} employes`);
      loadRolloverHistory();
    } catch {
      toast.error("Erreur lors du report");
    }
  };

  const filtered = filter === 'all' ? events : events.filter(e => e.employee_id === filter);

  const calendarEvents = filtered.map(e => ({
    id: e.id,
    title: e.title,
    start: e.start,
    end: e.end,
    backgroundColor: e.color,
    borderColor: e.color,
    textColor: '#fff',
    extendedProps: { leave_type: e.leave_type, employee_name: e.employee_name, days_used: e.days_used },
  }));

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
    <div className="space-y-4" data-testid="absence-calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('leaves.absence_calendar')}</h1>
          <p className="text-sm text-slate-500 mt-0.5">Vue d'ensemble des conges approuves</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleRollover} className="text-amber-700 border-amber-300 hover:bg-amber-50" data-testid="rollover-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Report annuel
          </Button>
        </div>
      </div>

      {/* Filter + Legend */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-400" />
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-48" data-testid="employee-filter">
                <SelectValue placeholder="Tous les employes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les employes</SelectItem>
                {employees.map(e => (
                  <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap gap-3 ml-auto">
            {Object.entries(TYPE_COLORS).filter(([k]) => !['conge'].includes(k)).map(([type, c]) => (
              <div key={type} className="flex items-center gap-1.5 text-xs">
                <span className="w-2.5 h-2.5 rounded-full" style={{backgroundColor: c.dot}}></span>
                <span className="text-slate-600 capitalize">{type}</span>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Calendar */}
      <Card className="p-4" data-testid="absence-calendar">
        {loading ? (
          <div className="text-center py-12 text-slate-400">{t('common.loading')}</div>
        ) : (
          <FullCalendar
            plugins={[dayGridPlugin]}
            initialView="dayGridMonth"
            locale={frLocale}
            events={calendarEvents}
            height="auto"
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,dayGridWeek',
            }}
            buttonText={{ today: "Aujourd'hui", month: 'Mois', week: 'Semaine' }}
            eventContent={(eventInfo) => (
              <div className="text-xs px-1 py-0.5 truncate font-medium" title={eventInfo.event.title}>
                {eventInfo.event.title}
              </div>
            )}
          />
        )}
      </Card>

      {/* Rollover history */}
      {rolloverHistory.length > 0 && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Historique des reports annuels</h3>
          <div className="space-y-2">
            {rolloverHistory.map(r => (
              <div key={r.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-slate-800">Report {r.year}</p>
                  <p className="text-xs text-slate-500">{new Date(r.executed_at).toLocaleDateString('fr-CH', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                </div>
                <span className="text-xs text-slate-500">{r.employees_count} employes</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
    </AdminLayout>
  );
};

export default AbsenceCalendarPage;
